/**
 * A project's governed settings: its overrides, laid over what the organisation governs for
 * everyone (shown beside each row). The project's value wins over the tenant's.
 */

import type { SettingsPolicy } from '@aioe/shared';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { SettingsPolicyEditor } from './settingsPolicy.tsx';

export function ProjectSettingsPolicy({ projectId, value, owner, onChange }: { projectId: string; value?: SettingsPolicy; owner: boolean; onChange: () => void }) {
  const effective = useLoad(() => routes.effectivePolicy(projectId).catch(() => undefined), [projectId]);
  const manifest = useLoad(() => routes.currentPolicyManifest().catch(() => undefined), []);
  const own: SettingsPolicy = value ?? { settings: {}, settingsMode: {} };
  // What the organisation governs for everyone: the effective block minus this project's own keys.
  const inherited: SettingsPolicy | undefined = effective.data?.policy.settings
    ? {
        settings: Object.fromEntries(Object.entries(effective.data.policy.settings).filter(([k]) => own.settings[k] === undefined)),
        settingsMode: Object.fromEntries(Object.entries(effective.data.policy.settingsMode ?? {}).filter(([k]) => own.settings[k] === undefined)),
      }
    : undefined;
  return (
    <SettingsPolicyEditor
      draftKey={`project.${projectId}.settings`}
      value={own}
      inherited={inherited}
      manifest={manifest.data?.manifest}
      canEdit={owner}
      title="Settings for this project"
      hint="Workbench and node settings this project sets over the organisation's. Locked: the value wins and people cannot change it. Suggested: people may change it."
      onSave={async (p) => {
        await routes.setProjectSettings(projectId, { settings: p.settings, settingsMode: p.settingsMode });
        onChange();
      }}
    />
  );
}
